import React from 'react'
import * as PropTypes from 'prop-types'
import {InferPropTypes} from '@modifi/typescript-utils'
import {Row, Col, Container, Paragraph, BaseBadgeIcon} from '@modifi/ui'
import LazyLoad from 'react-lazyload'

import Picture from '../../ui/cms-picture'
import {ImageWrapper, ImageCol, ImageContainer, Video} from './styles'
import {StyledH4, DefaultComponentWrapper} from '../common'
import LinkComponent from '../link-component'
import RequestDemoButton from '../../features/common/request-demo-button/RequestDemoButton'
import {ctaMapping} from '../constants'

const propTypes = {
  lateralImageUrl: PropTypes.string,
  lateralImagePosition: PropTypes.oneOf(['left', 'right']),
  lateralImageIsRounded: PropTypes.bool,
  headline: PropTypes.string,
  description: PropTypes.string,
  tagIcon: PropTypes.string,
  tagIconLabel: PropTypes.string,
  tagIconFontColorHexCode: PropTypes.string,
  tagIconBackgroundColorHexCode: PropTypes.string,
  ctaType: PropTypes.string,
  ctaLabel: PropTypes.string,
  ctaOpenInNewTab: PropTypes.bool,
  ctaTargetUrl: PropTypes.string,
  hasAnimationForImage: PropTypes.bool,
  lateralVideo: PropTypes.string,
  backgroundColor: PropTypes.string,
  mediaTextRatio: PropTypes.string,
  isCtaRequestDemoButton: PropTypes.bool,
  ctaAboveText: PropTypes.string,
}
const defaultProps = {
  lateralImageUrl: undefined,
  lateralImagePosition: 'left',
  lateralImageIsRounded: false,
  headline: undefined,
  description: undefined,
  tagIcon: undefined,
  tagIconLabel: undefined,
  tagIconFontColorHexCode: undefined,
  tagIconBackgroundColorHexCode: undefined,
  ctaType: undefined,
  ctaLabel: '',
  ctaTargetUrl: '',
  ctaOpenInNewTab: true,
  hasAnimationForImage: false,
  lateralVideo: undefined,
  backgroundColor: undefined,
  mediaTextRatio: 'text-6',
  isCtaRequestDemoButton: false,
  ctaAboveText: '',
}

const tagIconMap: Record<string, any> = {
  badge: BaseBadgeIcon,
}

export type TextWithLateralImageProps = InferPropTypes<typeof propTypes, typeof defaultProps>

export const TextWithLateralImage: React.FC<TextWithLateralImageProps> = ({
  lateralImageUrl,
  lateralImagePosition,
  lateralImageIsRounded,
  headline,
  description,
  tagIcon,
  tagIconLabel,
  tagIconFontColorHexCode,
  tagIconBackgroundColorHexCode,
  ctaType,
  ctaLabel,
  ctaTargetUrl,
  ctaOpenInNewTab,
  hasAnimationForImage,
  lateralVideo,
  backgroundColor,
  mediaTextRatio,
  isCtaRequestDemoButton,
  ctaAboveText,
}) => {
  const IconComponent = tagIcon ? tagIconMap[tagIcon] : null

  const textCol = mediaTextRatio ? Number(mediaTextRatio?.slice(-1)) : 6

  return (
    <DefaultComponentWrapper backgroundColor={backgroundColor}>
      <Container>
        <Row alignItems="center">
          <ImageCol md={12 - textCol} mdOrder={lateralImagePosition === 'right' ? 1 : 0}>
            {lateralVideo && (
              <Video
                src={lateralVideo}
                // @ts-expect-error type not generated
                type="video/mp4"
                autoPlay
                muted
                loop
                width="100%"
                poster={lateralImageUrl || ''}
              />
            )}
            {lateralImageUrl && !lateralVideo && (
              <ImageContainer>
                <LazyLoad>
                  <ImageWrapper
                    hasAnimationForImage={hasAnimationForImage || false}
                    isRounded={lateralImageIsRounded || false}
                  >
                    <Picture alt="image" src={lateralImageUrl} objectFit="fill" isBackgroundImage />
                  </ImageWrapper>
                </LazyLoad>
              </ImageContainer>
            )}
          </ImageCol>
          <Col md={textCol} mdOrder={lateralImagePosition === 'right' ? 0 : 1}>
            {IconComponent && (
              <Row>
                <Col>
                  <IconComponent
                    label={tagIconLabel}
                    fontColor={tagIconFontColorHexCode}
                    backgroundColor={tagIconBackgroundColorHexCode}
                    height={18}
                  />
                </Col>
              </Row>
            )}
            {headline && (
              <Row>
                <Col>
                  <StyledH4 mb={0}>{headline}</StyledH4>
                </Col>
              </Row>
            )}
            {description && (
              <Row mt={16}>
                <Col>
                  <Paragraph mt={0}>{description}</Paragraph>
                </Col>
              </Row>
            )}

            {ctaAboveText && (
              <Row mt={8}>
                <Col>
                  <Paragraph my={0}>{ctaAboveText}</Paragraph>
                </Col>
              </Row>
            )}

            {ctaType && (
              <Row mt={24}>
                <Col>
                  {isCtaRequestDemoButton ? (
                    <RequestDemoButton
                      buttonComponent={ctaMapping[ctaType]}
                      icon
                      url={ctaTargetUrl}
                    >
                      <span>{ctaLabel}</span>
                    </RequestDemoButton>
                  ) : (
                    <LinkComponent
                      targetUrl={ctaTargetUrl!}
                      type={ctaType}
                      label={ctaLabel!}
                      openInNewTab={ctaOpenInNewTab!}
                    />
                  )}
                </Col>
              </Row>
            )}
          </Col>
        </Row>
      </Container>
    </DefaultComponentWrapper>
  )
}

TextWithLateralImage.propTypes = propTypes

TextWithLateralImage.defaultProps = defaultProps

export default TextWithLateralImage
